import { useTranslation } from '@govard/i18n-core/react';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import type { ActivityItem } from '../../activity/api/activities.client.js';
import { useEntityActivities } from '../../activity/hooks/useEntityActivities.js';
import { describeActivity } from '../../activity/lib/describeActivity.js';
import { useMembers } from '../../tasks/hooks/useTaskLookups.js';

export interface CompanyHistoryItem {
  id: string;
  kind: string;
  text: string;
  date: string;
  actor: string;
}

// История компании — тот же источник, что у дровера «История» (`GET /activities`
// с entity=company). Сервер наследует привязки: в ленту попадают и события её
// контактов, сделок, задач и документов.
export function useCompanyHistory(companyId: string) {
  const { t } = useTranslation();
  const activities = useEntityActivities('company', companyId);
  const members = useMembers();
  const actor = (id: string): string => {
    if (id === 'system') return t('crm.features.activity.actor.system');
    const member = members.data?.find((item) => item.userId === id || item.id === id);
    return member?.name || member?.email || '';
  };
  const stageText = (item: ActivityItem): string | null => {
    const payload = item.payload ?? {};
    const from = payload['fromStage'] ?? payload['from'];
    const to = payload['toStage'] ?? payload['to'];
    if (typeof from !== 'string' || typeof to !== 'string') return null;
    return `${getEnumLabel('dealStage', from, t)} → ${getEnumLabel('dealStage', to, t)}`;
  };
  const items: CompanyHistoryItem[] = (activities.data ?? [])
    .map((item) => ({
      id: item.id,
      kind: item.kind,
      text: (item.kind === 'deal_stage_changed' ? stageText(item) : null) ?? describeActivity(item),
      date: item.occurredAt ?? item.createdAt,
      actor: actor(item.actor),
    }))
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  return {
    items,
    isLoading: activities.isLoading,
    error: activities.error,
    refetch: () => {
      void activities.refetch();
    },
  };
}
